import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Filter as FilterIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ObrigacaoModal from "../components/ObrigacaoModal";
import { useAuth } from "../context/AuthContext";
import {
  adminService,
  clienteService,
  obrigacaoService,
  recorrenciaService,
  setorService,
  tipoObrigacaoService,
  usuarioService,
} from "../services/api";

const Obrigacoes = () => {
  const location = useLocation();
  const { user } = useAuth();

  // ✅ CONFIGURAÇÃO DOS TAMANHOS DOS FILTROS - CSS PURO RESPONSIVO!
  // Valores de 0 a 100 (porcentagem da largura no desktop)
  // Mobile: sempre 100% (empilhados)
  // 💡 TESTE: Mude os valores e veja a diferença imediatamente!
  const TAMANHOS_FILTROS = {
    cliente: 20, // 20% - Cliente
    tipoObrigacao: 20, // 20% - Tipo de Obrigação
    setor: 15, // 15% - Setor
    status: 15, // 15% - Status
    dataInicio: 15, // 15% - Data início
    dataFim: 15, // 15% - Data fim
  };

  const [obrigacoes, setObrigacoes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [tiposObrigacao, setTiposObrigacao] = useState([]);
  const [setores, setSetores] = useState([]);
  const [escritorios, setEscritorios] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingObrigacao, setEditingObrigacao] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [paginacao, setPaginacao] = useState({
    page: 0,
    limit: 10,
    total: 0,
  });

  // Estados do formulário
  const [formData, setFormData] = useState({
    cliente_id: "",
    tipo_obrigacao_id: "",
    setor_id: "",
    comentarios: "",
    data_vencimento: "",
    status: "pendente",
  });

  // Estados dos filtros
  const [filters, setFilters] = useState({
    cliente_id: "",
    tipo_obrigacao_id: "",
    setor_id: "",
    status: "pendente", // Filtro padrão para obrigações pendentes
    data_inicio: "",
    data_fim: "",
    escritorio_id_filter: "", // Novo filtro para super admin
  });

  // Estados de feedback
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [errors, setErrors] = useState({});

  // Estados para seleção múltipla
  const [selectedObrigacoes, setSelectedObrigacoes] = useState([]);
  const [selectAllMode, setSelectAllMode] = useState(false); // false, 'partial', true

  // Estado para confirmação de exclusão
  const [confirmacaoExclusao, setConfirmacaoExclusao] = useState({
    aberto: false,
    obrigacaoId: null,
    obrigacaoDescricao: "",
  });

  // Função para calcular a data de vencimento baseada no tipo de obrigação
  const calcularDataVencimento = (tipoObrigacao) => {
    if (!tipoObrigacao) return "";

    const hoje = new Date();
    const anoAtual = hoje.getFullYear();
    const mesAtual = hoje.getMonth() + 1; // getMonth() retorna 0-11

    const diaRecorrente = tipoObrigacao.dia_recorrente;
    const mesRecorrente = tipoObrigacao.mes_recorrente;

    // Se tem dia e mês recorrente
    if (diaRecorrente && mesRecorrente) {
      let dataVencimento = new Date(anoAtual, mesRecorrente - 1, diaRecorrente);

      // Se a data já passou este ano, usar o próximo ano
      if (dataVencimento < hoje) {
        dataVencimento = new Date(
          anoAtual + 1,
          mesRecorrente - 1,
          diaRecorrente,
        );
      }

      // Formatar data sem problemas de timezone
      const ano = dataVencimento.getFullYear();
      const mes = String(dataVencimento.getMonth() + 1).padStart(2, "0");
      const dia = String(dataVencimento.getDate()).padStart(2, "0");
      return `${ano}-${mes}-${dia}`;
    }

    // Se tem apenas dia recorrente
    if (diaRecorrente) {
      let dataVencimento = new Date(anoAtual, mesAtual - 1, diaRecorrente);

      // Se o dia já passou este mês, usar o próximo mês
      if (dataVencimento < hoje) {
        const proximoMes = mesAtual === 12 ? 1 : mesAtual + 1;
        const proximoAno = mesAtual === 12 ? anoAtual + 1 : anoAtual;
        dataVencimento = new Date(proximoAno, proximoMes - 1, diaRecorrente);
      }

      // Formatar data sem problemas de timezone
      const ano = dataVencimento.getFullYear();
      const mes = String(dataVencimento.getMonth() + 1).padStart(2, "0");
      const dia = String(dataVencimento.getDate()).padStart(2, "0");
      return `${ano}-${mes}-${dia}`;
    }

    return "";
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: paginacao.page + 1,
        limit: paginacao.limit,
        ...filters,
      };

      // Remove filtros vazios
      Object.keys(params).forEach(
        (key) => params[key] === "" && delete params[key],
      );

      const response = await obrigacaoService.getAll(params);
      setObrigacoes(response.obrigacoes || []);
      setPaginacao((prev) => ({
        ...prev,
        total: response.pagination?.total || 0,
      }));
    } catch (error) {
      console.error("Erro ao carregar obrigações:", error);
      showSnackbar("Erro ao carregar obrigações", "error");
    } finally {
      setLoading(false);
    }
  }, [paginacao.page, paginacao.limit, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadClientes();
    loadTiposObrigacao();
    loadSetores();
    loadUsuarios();
    if (user?.is_super_admin) {
      loadEscritorios();
    }
  }, [user?.is_super_admin]);

  // Detectar query parameter para edição
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const editId = urlParams.get("edit");

    if (editId) {
      // Primeiro, tentar encontrar a obrigação na lista atual
      const obrigacaoParaEditar = obrigacoes.find(
        (o) => o.id === parseInt(editId),
      );

      if (obrigacaoParaEditar) {
        // Preencher o formulário e abrir o modal
        setFormData({
          cliente_id: obrigacaoParaEditar.cliente_id,
          tipo_obrigacao_id: obrigacaoParaEditar.tipo_obrigacao_id,
          setor_id: obrigacaoParaEditar.setor_id || "",
          comentarios: obrigacaoParaEditar.comentarios || "",
          data_vencimento: obrigacaoParaEditar.data_vencimento,
          status: obrigacaoParaEditar.status,
        });
        setEditingObrigacao(obrigacaoParaEditar);
        setOpenDialog(true);

        // Limpar o query parameter da URL
        window.history.replaceState({}, "", location.pathname);
      } else {
        // Se não encontrou na lista atual, buscar diretamente pela API
        const buscarObrigacaoEspecifica = async () => {
          try {
            const response = await obrigacaoService.getById(parseInt(editId));

            // Tentar diferentes estruturas de resposta
            const obrigacao =
              response.data?.obrigacao ||
              response.obrigacao ||
              response.data ||
              response;

            if (obrigacao && obrigacao.id) {
              // Preencher o formulário e abrir o modal
              setFormData({
                cliente_id: obrigacao.cliente_id,
                tipo_obrigacao_id: obrigacao.tipo_obrigacao_id,
                setor_id: obrigacao.setor_id || "",
                comentarios: obrigacao.comentarios || "",
                data_vencimento: obrigacao.data_vencimento,
                status: obrigacao.status,
              });
              setEditingObrigacao(obrigacao);
              setOpenDialog(true);

              // Limpar o query parameter da URL
              window.history.replaceState({}, "", location.pathname);
            } else {
              showSnackbar("Obrigação não encontrada", "error");
            }
          } catch (error) {
            console.error("Erro ao buscar obrigação específica:", error);
            showSnackbar("Erro ao carregar obrigação para edição", "error");
            // Limpar o query parameter da URL mesmo em caso de erro
            window.history.replaceState({}, "", location.pathname);
          }
        };

        buscarObrigacaoEspecifica();
      }
    }
  }, [location.search, location.pathname, obrigacoes]);

  const loadClientes = async () => {
    try {
      const response = await clienteService.getAll({ ativo: true });
      setClientes(response.clientes || []);
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
    }
  };

  const loadTiposObrigacao = async () => {
    try {
      const response = await tipoObrigacaoService.getAll({ limit: 1000 });
      setTiposObrigacao(response.tipos_obrigacoes || []);
    } catch (error) {
      console.error("Erro ao carregar tipos de obrigação:", error);
    }
  };

  const loadSetores = async () => {
    try {
      const response = await setorService.getAll({ limit: 100 });
      setSetores(response.setores || []);
    } catch (error) {
      console.error("Erro ao carregar setores:", error);
    }
  };

  const loadEscritorios = async () => {
    try {
      const response = await adminService.getEscritorios();
      // A API retorna diretamente um array, não um objeto com propriedade escritorios
      setEscritorios(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Erro ao carregar escritórios:", error);
    }
  };

  const loadUsuarios = async () => {
    try {
      const response = await usuarioService.getAll();
      setUsuarios(response.usuarios || []);
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = (obrigacao = null) => {
    if (obrigacao) {
      setEditingObrigacao(obrigacao);
      setFormData({
        cliente_id: obrigacao.cliente_id,
        tipo_obrigacao_id: obrigacao.tipo_obrigacao_id,
        setor_id: obrigacao.setor_id || "",
        comentarios: obrigacao.comentarios || "",
        data_vencimento: obrigacao.data_vencimento?.split("T")[0] || "",
        status: obrigacao.status,
      });
    } else {
      setEditingObrigacao(null);
      setFormData({
        cliente_id: "",
        tipo_obrigacao_id: "",
        setor_id: "",
        comentarios: "",
        data_vencimento: "",
        status: "pendente",
      });
    }
    setErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingObrigacao(null);
    setFormData({
      cliente_id: "",
      tipo_obrigacao_id: "",
      setor_id: "",
      comentarios: "",
      data_vencimento: "",
      status: "pendente",
    });
    setErrors({});
  };

  // Função para lidar com a mudança do tipo de obrigação
  const handleTipoObrigacaoChange = async (tipoObrigacaoId) => {
    // Encontrar o tipo de obrigação selecionado
    const tipoObrigacao = tiposObrigacao.find(
      (tipo) => tipo.id === parseInt(tipoObrigacaoId),
    );

    if (tipoObrigacao) {
      // Calcular a data de vencimento automaticamente
      const dataVencimento = calcularDataVencimento(tipoObrigacao);

      // Buscar recorrência para o cliente e tipo selecionados para puxar o setor
      let setorId = "";
      if (formData.cliente_id && tipoObrigacaoId) {
        try {
          const response = await recorrenciaService.getAll({
            cliente_id: formData.cliente_id,
            tipo_obrigacao_id: tipoObrigacaoId,
            limit: 1,
          });

          if (response.recorrencias && response.recorrencias.length > 0) {
            setorId = response.recorrencias[0].setor_id || "";
          }
        } catch (error) {
          console.error("Erro ao buscar recorrência:", error);
        }
      }

      setFormData((prev) => ({
        ...prev,
        tipo_obrigacao_id: tipoObrigacaoId,
        data_vencimento: dataVencimento,
        setor_id: setorId,
      }));
    } else {
      // Se nenhum tipo for selecionado, limpar a data e setor
      setFormData((prev) => ({
        ...prev,
        tipo_obrigacao_id: tipoObrigacaoId,
        data_vencimento: "",
        setor_id: "",
      }));
    }
  };

  // Função para lidar com a mudança do cliente
  const handleClienteChange = async (clienteId) => {
    setFormData((prev) => ({
      ...prev,
      cliente_id: clienteId,
    }));

    // Se já tem tipo de obrigação selecionado, buscar setor da recorrência
    if (clienteId && formData.tipo_obrigacao_id) {
      try {
        const response = await recorrenciaService.getAll({
          cliente_id: clienteId,
          tipo_obrigacao_id: formData.tipo_obrigacao_id,
          limit: 1,
        });

        if (response.recorrencias && response.recorrencias.length > 0) {
          const setorId = response.recorrencias[0].setor_id || "";
          setFormData((prev) => ({
            ...prev,
            setor_id: setorId,
          }));
        }
      } catch (error) {
        console.error("Erro ao buscar recorrência:", error);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.cliente_id) {
      newErrors.cliente_id = "Cliente é obrigatório";
    }

    if (!formData.tipo_obrigacao_id) {
      newErrors.tipo_obrigacao_id = "Tipo de obrigação é obrigatório";
    }

    if (!formData.data_vencimento) {
      newErrors.data_vencimento = "Data de vencimento é obrigatória";
    }

    if (!formData.status) {
      newErrors.status = "Status é obrigatório";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (salvando || !validateForm()) return; // Evita cliques duplos

    try {
      setSalvando(true);
      // Preparar dados para envio
      const dadosParaEnvio = {
        ...formData,
      };

      console.log("Dados sendo enviados:", dadosParaEnvio); // Debug

      if (editingObrigacao) {
        // Verificar se o status está sendo alterado para "enviado" ou "enviado_atrasado"
        const statusAlteradoParaEnviado =
          editingObrigacao.status !== "enviado" &&
          editingObrigacao.status !== "enviado_atrasado" &&
          (formData.status === "enviado" ||
            formData.status === "enviado_atrasado");

        if (statusAlteradoParaEnviado) {
          // Se o status está sendo alterado para "enviado" ou "enviado_atrasado", usar updateStatus
          const response = await obrigacaoService.updateStatus(
            editingObrigacao.id,
            formData.status,
          );

          // Mostrar mensagem baseada na resposta do backend
          if (response.data.proxima_obrigacao_gerada) {
            const dataVencimento = formatDate(
              response.data.proxima_obrigacao_gerada.data_vencimento,
            );
            showSnackbar(
              `Obrigação atualizada com sucesso! Próxima obrigação criada automaticamente com vencimento em ${dataVencimento}`,
            );

            // Recarregar os dados para mostrar a nova obrigação
            setTimeout(() => {
              loadData();
            }, 500);
          } else {
            showSnackbar(
              response.message || "Obrigação atualizada com sucesso",
            );
          }
        } else {
          // Para outras alterações, usar update normal
          await obrigacaoService.update(editingObrigacao.id, dadosParaEnvio);
          showSnackbar("Obrigação atualizada com sucesso");
        }
      } else {
        await obrigacaoService.create(dadosParaEnvio);
        showSnackbar("Obrigação criada com sucesso");
      }
      handleCloseDialog();
      loadData();

      // Se veio da agenda, voltar para lá
      if (location.search.includes("edit")) {
        window.history.replaceState({}, "", "/agenda");
        window.location.href = "/agenda";
      }
    } catch (error) {
      console.error("Erro ao salvar obrigação:", error);
      showSnackbar(error.message || "Erro ao salvar obrigação", "error");
    } finally {
      setSalvando(false);
    }
  };

  const handleDelete = (obrigacao) => {
    const descricao = `${obrigacao.Cliente?.nome || "Cliente"} - ${obrigacao.TipoObrigacao?.descricao || "Obrigação"}`;
    setConfirmacaoExclusao({
      aberto: true,
      obrigacaoId: obrigacao.id,
      obrigacaoDescricao: descricao,
    });
  };

  const confirmarExclusao = async () => {
    if (excluindo) return; // Evita cliques duplos

    try {
      setExcluindo(true);
      await obrigacaoService.delete(confirmacaoExclusao.obrigacaoId);
      showSnackbar("Obrigação excluída com sucesso");
      setConfirmacaoExclusao({
        aberto: false,
        obrigacaoId: null,
        obrigacaoDescricao: "",
      });
      loadData();
    } catch (error) {
      console.error("Erro ao excluir obrigação:", error);
      showSnackbar(error.message || "Erro ao excluir obrigação", "error");
    } finally {
      setExcluindo(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await obrigacaoService.updateStatus(id, newStatus);

      // Atualizar o estado local imediatamente
      setObrigacoes((prevObrigacoes) =>
        prevObrigacoes.map((obrigacao) =>
          obrigacao.id === id ? { ...obrigacao, status: newStatus } : obrigacao,
        ),
      );

      // Mostrar mensagem baseada na resposta do backend
      if (response.data.proxima_obrigacao_gerada) {
        const dataVencimento = formatDate(
          response.data.proxima_obrigacao_gerada.data_vencimento,
        );
        showSnackbar(
          `Status atualizado com sucesso! Próxima obrigação criada automaticamente com vencimento em ${dataVencimento}`,
        );

        // Recarregar os dados para mostrar a nova obrigação
        setTimeout(() => {
          loadData();
        }, 500); // Pequeno delay para garantir que o backend processou
      } else {
        showSnackbar(response.message || "Status atualizado com sucesso");

        // Recarregar os dados quando há mudança na data de envio
        // (status "enviado", "enviado_atrasado" ou "pendente")
        if (
          newStatus === "enviado" ||
          newStatus === "enviado_atrasado" ||
          newStatus === "pendente"
        ) {
          setTimeout(() => {
            loadData();
          }, 500); // Pequeno delay para garantir que o backend processou
        }
      }
    } catch (error) {
      console.error("Erro ao atualizar status:", error);
      showSnackbar(error.message || "Erro ao atualizar status", "error");
    }
  };

  const handlePageChange = (event, newPage) => {
    setPaginacao((prev) => ({ ...prev, page: newPage }));
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPaginacao((prev) => ({ ...prev, page: 0 }));
  };

  const clearFilters = () => {
    setFilters({
      cliente_id: "",
      tipo_obrigacao_id: "",
      setor_id: "",
      status: "pendente", // Manter filtro padrão para obrigações pendentes
      data_inicio: "",
      data_fim: "",
      escritorio_id_filter: "",
    });
    setPaginacao((prev) => ({ ...prev, page: 0 }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pendente":
        return "warning";
      case "em_andamento":
        return "primary";
      case "enviado":
        return "info";
      case "enviado_atrasado":
        return "error";
      case "pago":
        return "success";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "pendente":
        return "Pendente";
      case "em_andamento":
        return "Em andamento";
      case "enviado":
        return "Enviado";
      case "enviado_atrasado":
        return "Enviado com atraso";
      case "pago":
        return "Pago";
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";

    // Se vier no formato yyyy-mm-dd, monta manualmente
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split("-");
      return `${day}/${month}/${year}`;
    }

    // Se vier como string ISO (com T e Z), extrai apenas a parte da data
    if (dateString.includes("T")) {
      const datePart = dateString.split("T")[0];
      const [year, month, day] = datePart.split("-");
      return `${day}/${month}/${year}`;
    }

    // Se vier como objeto Date ou string ISO, converte para pt-BR
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Retorna a string original se não conseguir converter
      }
      return date.toLocaleDateString("pt-BR");
    } catch (error) {
      return dateString; // Retorna a string original em caso de erro
    }
  };

  // Funções para seleção múltipla
  const handleSelectObrigacao = (obrigacaoId) => {
    setSelectedObrigacoes((prev) => {
      if (prev.includes(obrigacaoId)) {
        return prev.filter((id) => id !== obrigacaoId);
      } else {
        return [...prev, obrigacaoId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAllMode === true) {
      // Se todas estão selecionadas, desmarcar todas
      setSelectedObrigacoes([]);
      setSelectAllMode(false);
    } else {
      // Selecionar todas as obrigações da página atual
      const idsAtual = obrigacoes.map((o) => o.id);
      setSelectedObrigacoes(idsAtual);
      setSelectAllMode(true);
    }
  };

  // Atualizar o estado do selectAll baseado na seleção atual
  const updateSelectAllMode = useCallback(() => {
    const idsAtual = obrigacoes.map((o) => o.id);
    const selectedCount = selectedObrigacoes.filter((id) =>
      idsAtual.includes(id),
    ).length;

    if (selectedCount === 0) {
      setSelectAllMode(false);
    } else if (selectedCount === idsAtual.length) {
      setSelectAllMode(true);
    } else {
      setSelectAllMode("partial");
    }
  }, [obrigacoes, selectedObrigacoes]);

  // Executar updateSelectAllMode quando obrigações ou seleção mudarem
  useEffect(() => {
    updateSelectAllMode();
  }, [obrigacoes, selectedObrigacoes, updateSelectAllMode]);

  // Limpar seleção quando filtros mudarem
  useEffect(() => {
    setSelectedObrigacoes([]);
    setSelectAllMode(false);
  }, [filters]);

  // Função para alterar status em massa
  const handleBulkStatusChange = async (newStatus) => {
    if (selectedObrigacoes.length === 0) {
      showSnackbar("Nenhuma obrigação selecionada", "warning");
      return;
    }

    try {
      const response = await obrigacaoService.updateStatusBulk(
        selectedObrigacoes,
        newStatus,
      );

      // Mostrar mensagem de sucesso
      const qtdRecorrentes = response.data?.obrigacoes_recorrentes_criadas || 0;
      let message = `${selectedObrigacoes.length} obrigações atualizadas com sucesso`;
      if (qtdRecorrentes > 0) {
        message += ` e ${qtdRecorrentes} obrigações recorrentes criadas automaticamente`;
      }

      showSnackbar(message);

      // Limpar seleção e recarregar dados
      setSelectedObrigacoes([]);
      setSelectAllMode(false);
      loadData();
    } catch (error) {
      console.error("Erro ao alterar status em massa:", error);
      showSnackbar(
        error.message || "Erro ao alterar status das obrigações",
        "error",
      );
    }
  };

  // Componente para exibir prioridade com cor
  // const ExibirPrioridade = ({ prioridade }) => {
  //   if (!prioridade) {
  //     return (
  //       <Typography variant="body2" color="text.secondary">
  //         Sem prioridade
  //       </Typography>
  //     );
  //   }

  //   // Se a prioridade tem cor definida, usar ela como background
  //   if (prioridade.cor) {
  //     return (
  //       <Chip
  //         label={prioridade.nome}
  //         size="small"
  //         sx={{
  //           backgroundColor: prioridade.cor,
  //           color: getContrastColor(prioridade.cor),
  //           fontWeight: 600,
  //           border: `1px solid ${prioridade.cor}`,
  //           "&:hover": {
  //           backgroundColor: prioridade.cor,
  //           opacity: 0.9,
  //           },
  //         }}
  //       />
  //     );
  //   }

  //   // Se não tem cor, usar o estilo padrão
  //   return (
  //     <Chip
  //       label={prioridade.nome}
  //       size="small"
  //       color="primary"
  //       variant="outlined"
  //     />
  //   );
  // };

  // Função para determinar a cor do texto baseada na cor de fundo - Comentado temporariamente
  // const getContrastColor = (hexColor) => {
  //   if (!hexColor) return "#000000";

  //   // Remove o # se existir
  //   const hex = hexColor.replace("#", "");

  //   // Converte para RGB
  //   const r = parseInt(hex.substr(0, 2), 16);
  //   const g = parseInt(hex.substr(2, 2), 16);
  //   const b = parseInt(hex.substr(4, 2), 16);

  //   // Calcula a luminância
  //   const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  //   // Retorna branco para fundos escuros, preto para fundos claros
  //   return luminance > 0.5 ? "#000000" : "#FFFFFF";
  // };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" sx={{ fontWeight: 600, color: "#333" }}>
          Obrigações
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ mr: 2 }}
          >
            Filtros
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ backgroundColor: "#1976d2" }}
          >
            Nova obrigação
          </Button>
        </Box>
      </Box>

      {/* Barra de ações em massa */}
      {selectedObrigacoes.length > 0 && (
        <Card sx={{ mb: 3, p: 2, backgroundColor: "#f5f5f5" }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {selectedObrigacoes.length} obrigação
              {selectedObrigacoes.length !== 1 ? "ões" : ""} selecionada
              {selectedObrigacoes.length !== 1 ? "s" : ""}
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleBulkStatusChange("enviado")}
                sx={{ color: "#1976d2", borderColor: "#1976d2" }}
              >
                Marcar como enviado
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleBulkStatusChange("enviado_atrasado")}
                sx={{ color: "#d32f2f", borderColor: "#d32f2f" }}
              >
                Marcar como enviado com atraso
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleBulkStatusChange("pago")}
                sx={{ color: "#2e7d32", borderColor: "#2e7d32" }}
              >
                Marcar como pago
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setSelectedObrigacoes([]);
                  setSelectAllMode(false);
                }}
                sx={{ color: "#666", borderColor: "#666" }}
              >
                Cancelar seleção
              </Button>
            </Box>
          </Box>
        </Card>
      )}

      {/* Filtros */}
      {showFilters && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>

          {/* ✅ SOLUÇÃO CSS PURA - Funciona em qualquer versão do Material-UI */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: "100%",
              flexWrap: { xs: "wrap", md: "nowrap" }, // Mobile: quebra linha, Desktop: mesma linha
              alignItems: "center",
            }}
          >
            {/* Cliente */}
            <Box
              sx={{
                flex: `0 1 ${TAMANHOS_FILTROS.cliente}%`,
                minWidth: { xs: "100%", md: "150px" },
                maxWidth: { md: `${TAMANHOS_FILTROS.cliente}%` },
              }}
            >
              <TextField
                fullWidth
                select
                label="Cliente"
                value={filters.cliente_id}
                onChange={(e) =>
                  handleFilterChange("cliente_id", e.target.value)
                }
              >
                <MenuItem value="">Todos</MenuItem>
                {clientes.map((cliente) => (
                  <MenuItem key={cliente.id} value={cliente.id}>
                    {cliente.nome}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Tipo de Obrigação */}
            <Box
              sx={{
                flex: `0 1 ${TAMANHOS_FILTROS.tipoObrigacao}%`,
                minWidth: { xs: "100%", md: "150px" },
                maxWidth: { md: `${TAMANHOS_FILTROS.tipoObrigacao}%` },
              }}
            >
              <TextField
                fullWidth
                select
                label="Tipo de obrigação"
                value={filters.tipo_obrigacao_id}
                onChange={(e) =>
                  handleFilterChange("tipo_obrigacao_id", e.target.value)
                }
              >
                <MenuItem value="">Todos</MenuItem>
                {tiposObrigacao.map((tipo) => (
                  <MenuItem key={tipo.id} value={tipo.id}>
                    {tipo.descricao}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Setor */}
            <Box
              sx={{
                flex: `0 1 ${TAMANHOS_FILTROS.setor}%`,
                minWidth: { xs: "100%", md: "120px" },
                maxWidth: { md: `${TAMANHOS_FILTROS.setor}%` },
              }}
            >
              <TextField
                fullWidth
                select
                label="Setor"
                value={filters.setor_id}
                onChange={(e) => handleFilterChange("setor_id", e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {setores.map((setor) => (
                  <MenuItem key={setor.id} value={setor.id}>
                    {setor.descricao}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Status */}
            <Box
              sx={{
                flex: `0 1 ${TAMANHOS_FILTROS.status}%`,
                minWidth: { xs: "100%", md: "120px" },
                maxWidth: { md: `${TAMANHOS_FILTROS.status}%` },
              }}
            >
              <TextField
                fullWidth
                select
                label="Status"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="pendente">Pendente</MenuItem>
                <MenuItem value="em_andamento">Em andamento</MenuItem>
                <MenuItem value="enviado">Enviado</MenuItem>
                <MenuItem value="enviado_atrasado">Enviado com atraso</MenuItem>
                <MenuItem value="pago">Pago</MenuItem>
              </TextField>
            </Box>

            {/* Data Início */}
            <Box
              sx={{
                flex: `0 1 ${TAMANHOS_FILTROS.dataInicio}%`,
                minWidth: { xs: "100%", md: "120px" },
                maxWidth: { md: `${TAMANHOS_FILTROS.dataInicio}%` },
              }}
            >
              <TextField
                fullWidth
                label="Data início"
                type="date"
                value={filters.data_inicio}
                onChange={(e) =>
                  handleFilterChange("data_inicio", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {/* Data Fim */}
            <Box
              sx={{
                flex: `0 1 ${TAMANHOS_FILTROS.dataFim}%`,
                minWidth: { xs: "100%", md: "120px" },
                maxWidth: { md: `${TAMANHOS_FILTROS.dataFim}%` },
              }}
            >
              <TextField
                fullWidth
                label="Data fim"
                type="date"
                value={filters.data_fim}
                onChange={(e) => handleFilterChange("data_fim", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {/* Filtro de Escritório - apenas para super admin */}
            {user?.is_super_admin && (
              <Box
                sx={{
                  flex: `0 1 ${TAMANHOS_FILTROS.cliente}%`, // Mesmo tamanho do cliente
                  minWidth: { xs: "100%", md: "150px" },
                  maxWidth: { md: `${TAMANHOS_FILTROS.cliente}%` },
                }}
              >
                <TextField
                  fullWidth
                  select
                  label="Escritório"
                  value={filters.escritorio_id_filter}
                  onChange={(e) =>
                    handleFilterChange("escritorio_id_filter", e.target.value)
                  }
                >
                  <MenuItem value="">Todos os escritórios</MenuItem>
                  {escritorios.map((escritorio) => (
                    <MenuItem key={escritorio.id} value={escritorio.id}>
                      {escritorio.nome}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}
          </Box>

          <Box mt={3}>
            <Button onClick={clearFilters} variant="outlined" size="small">
              Redefinir filtros
            </Button>
          </Box>
        </Paper>
      )}

      {/* Tabela */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectAllMode === "partial"}
                    checked={selectAllMode === true}
                    onChange={handleSelectAll}
                    color="primary"
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  Tipo de obrigação
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Vencimento</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Setor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Data de envio</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Comentários</TableCell>
                {/* Prioridade */}
                {/* <TableCell sx={{ fontWeight: 600 }}>Prioridade</TableCell> */}
                {user?.is_super_admin && (
                  <TableCell sx={{ fontWeight: 600 }}>Escritório</TableCell>
                )}
                <TableCell sx={{ fontWeight: 600 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={user?.is_super_admin ? 10 : 9}
                    align="center"
                  >
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : obrigacoes.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={user?.is_super_admin ? 10 : 9}
                    align="center"
                  >
                    Nenhuma obrigação encontrada
                  </TableCell>
                </TableRow>
              ) : (
                obrigacoes.map((obrigacao) => (
                  <TableRow key={obrigacao.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedObrigacoes.includes(obrigacao.id)}
                        onChange={() => handleSelectObrigacao(obrigacao.id)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>{obrigacao.cliente?.nome || "-"}</TableCell>
                    <TableCell>
                      {obrigacao.tipo_obrigacao?.descricao || "-"}
                    </TableCell>
                    <TableCell>
                      {formatDate(obrigacao.data_vencimento)}
                    </TableCell>
                    <TableCell>{obrigacao.setor?.descricao || "-"}</TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={obrigacao.status}
                          onChange={(e) =>
                            handleStatusChange(obrigacao.id, e.target.value)
                          }
                          displayEmpty
                          renderValue={(selected) => (
                            <Chip
                              label={getStatusLabel(selected)}
                              color={getStatusColor(selected)}
                              size="small"
                            />
                          )}
                        >
                          <MenuItem value="pendente">Pendente</MenuItem>
                          <MenuItem value="em_andamento">Em andamento</MenuItem>
                          <MenuItem value="enviado">Enviado</MenuItem>
                          <MenuItem value="enviado_atrasado">
                            Enviado com atraso
                          </MenuItem>
                          <MenuItem value="pago">Pago</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      {obrigacao.data_envio ? (
                        <Box>
                          <Typography variant="body2">
                            {formatDate(obrigacao.data_envio)}
                          </Typography>
                          {obrigacao.enviado_por ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem" }}
                            >
                              por {obrigacao.enviado_por.nome}
                            </Typography>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem", fontStyle: "italic" }}
                            >
                              (usuário não identificado)
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      {obrigacao.comentarios ? (
                        <Tooltip title={obrigacao.comentarios} arrow>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 150,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: "help",
                            }}
                          >
                            {obrigacao.comentarios}
                          </Typography>
                        </Tooltip>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    {/* Prioridade */}
                    {/* <TableCell>
                      <ExibirPrioridade prioridade={obrigacao.prioridade} />
                    </TableCell> */}
                    {user?.is_super_admin && (
                      <TableCell>
                        {obrigacao.cliente?.escritorio?.nome || "-"}
                      </TableCell>
                    )}
                    <TableCell>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(obrigacao)}
                          color="info"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        {obrigacao.status === "pago" ? (
                          <span>
                            <IconButton size="small" disabled color="error">
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(obrigacao)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginação */}
        <TablePagination
          component="div"
          count={paginacao.total}
          page={paginacao.page}
          onPageChange={handlePageChange}
          rowsPerPage={paginacao.limit}
          onRowsPerPageChange={(event) => {
            setPaginacao((prev) => ({
              ...prev,
              limit: parseInt(event.target.value, 10),
              page: 0,
            }));
          }}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count}`
          }
        />
      </Card>

      {/* Modal para criar/editar obrigação */}
      <ObrigacaoModal
        open={openDialog}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        clientes={clientes}
        tiposObrigacao={tiposObrigacao}
        setores={setores}
        usuariosEscritorio={usuarios}
        editingObrigacao={editingObrigacao}
        salvando={salvando}
        errors={errors}
        onClienteChange={handleClienteChange}
        onTipoObrigacaoChange={handleTipoObrigacaoChange}
      />

      {/* Modal de confirmação de exclusão */}
      <Dialog
        open={confirmacaoExclusao.aberto}
        onClose={() =>
          setConfirmacaoExclusao({
            aberto: false,
            obrigacaoId: null,
            obrigacaoDescricao: "",
          })
        }
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir a obrigação "
            {confirmacaoExclusao.obrigacaoDescricao}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmacaoExclusao({
                aberto: false,
                obrigacaoId: null,
                obrigacaoDescricao: "",
              })
            }
            color="primary"
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmarExclusao}
            color="error"
            variant="contained"
            disabled={excluindo}
          >
            {excluindo ? <CircularProgress size={20} /> : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Obrigacoes;
